import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { EnumTheme } from "utils/enums";

export const SpaceBackground = () => {
  const { theme } = useTheme();
  const { t } = useApp();
  const isDark = theme.id === EnumTheme.Dark;

  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const updateScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;
      setScrollProgress(progress);
    };

    updateScroll();
    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateScroll);
      window.removeEventListener("resize", updateScroll);
    };
  }, []);

  // Meteor shower spanning across upper atmospheric coordinates
  const meteors = useMemo(() => {
    return [
      { id: 1, top: 6, left: 88, duration: 1.1, delay: 1.5, length: 240 },
      { id: 2, top: 18, left: 45, duration: 0.9, delay: 5.2, length: 190 },
      { id: 3, top: 32, left: 95, duration: 1.2, delay: 9.8, length: 260 },
      { id: 4, top: 46, left: 70, duration: 0.85, delay: 13.4, length: 210 },
      { id: 5, top: 60, left: 35, duration: 1.0, delay: 17.0, length: 180 },
      { id: 6, top: 22, left: 20, duration: 0.95, delay: 21.6, length: 230 },
      { id: 7, top: 68, left: 85, duration: 1.15, delay: 25.3, length: 250 },
      { id: 8, top: 12, left: 62, duration: 1.05, delay: 29.7, length: 200 },
    ];
  }, []);

  // Carruthers Atmospheric Layer Boundary Markers (100% i18n localized)
  const atmosphericMarkers = useMemo(
    () => [
      {
        progress: 0.04,
        km: "10,000",
        label: t.atmosphere?.exosphere || "EXOSPHERE",
        sub: t.atmosphere?.exosphere_sub || "ORBITAL INSERTION // SATELLITE BELT",
      },
      {
        progress: 0.22,
        km: "600",
        label: t.atmosphere?.thermosphere || "THERMOSPHERE",
        sub: t.atmosphere?.thermosphere_sub || "KARMAN LINE // AURORAL IONOSPHERE",
      },
      {
        progress: 0.44,
        km: "85",
        label: t.atmosphere?.mesosphere || "MESOSPHERE",
        sub: t.atmosphere?.mesosphere_sub || "METEOR ABLATION ZONE",
      },
      {
        progress: 0.66,
        km: "50",
        label: t.atmosphere?.stratosphere || "STRATOSPHERE",
        sub: t.atmosphere?.stratosphere_sub || "SUPERSONIC OZONE HORIZON",
      },
      {
        progress: 0.85,
        km: "12",
        label: t.atmosphere?.troposphere || "TROPOSPHERE",
        sub: t.atmosphere?.troposphere_sub || "DENSE CLOUD DECK // MAX-Q",
      },
      {
        progress: 0.98,
        km: "00",
        label: t.atmosphere?.launchpad || "LAUNCHPAD // T-00:00",
        sub: t.atmosphere?.launchpad_sub || "SURFACE COMPLEX 39A // PAD READY",
      },
    ],
    [t.atmosphere]
  );

  const gridDotColor = isDark
    ? "rgba(177, 197, 255, 0.055)"
    : "rgba(53, 89, 168, 0.045)";

  const gridLineColor = isDark
    ? "rgba(255, 255, 255, 0.018)"
    : "rgba(25, 27, 34, 0.018)";

  // Subtle vertical gradient where the bottom (ground/troposphere) is slightly brighter than deep space
  const atmosphereOverlay = isDark
    ? `linear-gradient(180deg, 
        rgba(12, 13, 19, 0.92) 0%, 
        rgba(19, 21, 30, 0.72) 50%, 
        rgba(34, 42, 64, ${0.22 + scrollProgress * 0.24}) 85%, 
        rgba(56, 68, 98, ${0.28 + scrollProgress * 0.26}) 100%)`
    : `linear-gradient(180deg, 
        rgba(232, 235, 248, 0.78) 0%, 
        rgba(244, 245, 255, 0.55) 55%, 
        rgba(255, 255, 255, ${0.65 + scrollProgress * 0.3}) 88%, 
        rgba(255, 250, 245, 0.95) 100%)`;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        backgroundColor: theme.color.background.color,
        transition: "background-color 0.4s ease-in-out",
      }}
    >
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes wide-meteor {
              0% {
                transform: rotate(-35deg) translateX(0);
                opacity: 0;
              }
              8% {
                opacity: 0.85;
              }
              35% {
                opacity: 0.6;
              }
              100% {
                transform: rotate(-35deg) translateX(-1100px);
                opacity: 0;
              }
            }
            @keyframes orbit-rotate {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `,
        }}
      />

      {/* Subtle Carruthers Atmospheric Gradient Wash (Ground slightly brighter) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: atmosphereOverlay,
          transition: "background 0.2s ease",
        }}
      />

      {/* Subtle Squares and Dots Matrix Pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(${gridDotColor} 1px, transparent 1px),
            linear-gradient(to right, ${gridLineColor} 1px, transparent 1px),
            linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)
          `,
          backgroundSize: "28px 28px, 112px 112px, 112px 112px",
          backgroundPosition: `0 -${scrollProgress * 180}px, 0 -${scrollProgress * 180}px, 0 -${scrollProgress * 180}px`,
        }}
      />

      {/* =========================================================================
          CARRUTHERS ATMOSPHERIC LAYER HORIZON LINES & KM MARKERS
          Parallax-synced horizontal altitude strata that pass through the viewport
          as you descend from 10,000 KM Exosphere down to 0 KM Launchpad.
         ========================================================================= */}
      {atmosphericMarkers.map((layer, idx) => {
        const delta = (layer.progress - scrollProgress) * 165;
        const topPercent = 50 + delta;
        const isVisible = topPercent > -15 && topPercent < 115;

        if (!isVisible) return null;

        return (
          <Box
            key={idx}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${topPercent}%`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: theme.spacing.sm, md: theme.spacing.xl },
              opacity: isDark ? 0.28 : 0.35,
              transform: "translateY(-50%)",
              transition: "top 0.08s linear",
            }}
          >
            {/* Left Altitude Tag */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: theme.spacing.xs,
                px: theme.spacing.xs,
                py: 0.25,
                backgroundColor: theme.color.background.accent,
                border: `1px solid ${theme.color.border.color}`,
                borderRadius: theme.border.square,
                fontFamily: '"Space Mono", monospace',
                fontSize: theme.font.xxxs,
                fontWeight: theme.font.bold,
                color: theme.color.primary.color,
                letterSpacing: "0.1em",
              }}
            >
              <span>
                {t.atmosphere?.alt_prefix || "ALT"} {"//"} {layer.km}{" "}
                {t.atmosphere?.alt_unit || "KM"}
              </span>
            </Box>

            {/* Dashed Atmospheric Boundary Horizon Line */}
            <Box
              sx={{
                flexGrow: 1,
                mx: theme.spacing.md,
                borderBottom: `1px dashed ${theme.color.border.color}`,
                position: "relative",
              }}
            />

            {/* Right Layer Designation */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing.sm,
                fontFamily: '"Space Mono", monospace',
                fontSize: theme.font.xxxs,
                letterSpacing: "0.12em",
                color: theme.color.font.accent,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: theme.font.bold,
                  color: theme.color.font.color,
                }}
              >
                [{layer.label}]
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                {layer.sub}
              </Box>
            </Box>
          </Box>
        );
      })}

      {/* =========================================================================
          16-BIT TACTILE TECHNICAL VECTOR SUN & MOON (MATCHING ROCKET ARTISTIC STYLE)
          Crisp geometric vector linework, telemetry rings, and theme strokes.
         ========================================================================= */}
      <Box
        sx={{
          position: "fixed",
          top: { xs: 68, md: 80 },
          right: { xs: 16, sm: 32, md: 56, lg: 88 },
          width: { xs: 96, md: 120 },
          height: { xs: 96, md: 120 },
          pointerEvents: "none",
          zIndex: 0,
          transform: `translateY(-${scrollProgress * 65}px)`,
          transition: "transform 0.15s ease-out",
        }}
      >
        {/* THE SUN (Light Mode) — Tactile Technical Vector Illustration */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            transform: isDark
              ? "translateY(-180%) scale(0.65)"
              : "translateY(0) scale(1)",
            opacity: isDark ? 0 : 0.95,
            transition:
              "transform 0.85s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s ease",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 120 120"
            sx={{ width: "100%", height: "100%", overflow: "visible" }}
          >
            {/* Outer Rotating Telemetry Ring */}
            <g
              style={{
                transformOrigin: "60px 60px",
                animation: "orbit-rotate 48s linear infinite",
              }}
            >
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke={theme.color.border.color}
                strokeWidth="1.5"
                strokeDasharray="6 6"
              />
              {/* 8 Cardinal & Diagonal Geometric Solar Rays */}
              <polygon
                points="60,2 56,12 64,12"
                fill={theme.color.secondary.accent}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <polygon
                points="60,118 56,108 64,108"
                fill={theme.color.secondary.accent}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <polygon
                points="2,60 12,56 12,64"
                fill={theme.color.secondary.accent}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <polygon
                points="118,60 108,56 108,64"
                fill={theme.color.secondary.accent}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <rect
                x="17"
                y="17"
                width="6"
                height="6"
                transform="rotate(45 20 20)"
                fill={theme.color.primary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <rect
                x="97"
                y="17"
                width="6"
                height="6"
                transform="rotate(45 100 20)"
                fill={theme.color.primary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <rect
                x="17"
                y="97"
                width="6"
                height="6"
                transform="rotate(45 20 100)"
                fill={theme.color.primary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <rect
                x="97"
                y="97"
                width="6"
                height="6"
                transform="rotate(45 100 100)"
                fill={theme.color.primary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
            </g>

            {/* Hard Tactile Offset Shadow Disc */}
            <circle cx="63" cy="63" r="34" fill={theme.color.font.color} opacity="0.18" />

            {/* Core Solar Disc */}
            <circle
              cx="60"
              cy="60"
              r="34"
              fill="#ffc038"
              stroke={theme.color.font.color}
              strokeWidth="2"
            />

            {/* Inner Concentric Core Ring & Architectural Hatching */}
            <circle
              cx="60"
              cy="60"
              r="26"
              fill="#fff3b0"
              stroke={theme.color.font.color}
              strokeWidth="1.5"
              strokeDasharray="10 4"
            />
            <line
              x1="32"
              y1="52"
              x2="88"
              y2="52"
              stroke={theme.color.font.color}
              strokeWidth="1.2"
              opacity="0.35"
            />
            <line
              x1="36"
              y1="68"
              x2="84"
              y2="68"
              stroke={theme.color.font.color}
              strokeWidth="1.2"
              opacity="0.35"
            />

            {/* Telemetry Crosshair Center */}
            <circle
              cx="60"
              cy="60"
              r="4"
              fill={theme.color.secondary.accent}
              stroke={theme.color.font.color}
              strokeWidth="1.5"
            />
          </Box>
        </Box>

        {/* THE MOON (Dark Mode) — Tactile Technical Vector Illustration */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            transform: isDark
              ? "translateY(0) scale(1)"
              : "translateY(-180%) scale(0.65)",
            opacity: isDark ? 0.95 : 0,
            transition:
              "transform 0.85s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s ease",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 120 120"
            sx={{ width: "100%", height: "100%", overflow: "visible" }}
          >
            {/* Outer Orbital Radar Ring */}
            <g
              style={{
                transformOrigin: "60px 60px",
                animation: "orbit-rotate 60s linear infinite",
              }}
            >
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                stroke={theme.color.border.color}
                strokeWidth="1.5"
                strokeDasharray="4 8"
              />
              {/* Orbital Satellite Node */}
              <rect
                x="56"
                y="6"
                width="8"
                height="8"
                fill={theme.color.primary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
              <circle
                cx="60"
                cy="110"
                r="3.5"
                fill={theme.color.secondary.color}
                stroke={theme.color.font.color}
                strokeWidth="1.5"
              />
            </g>

            {/* Hard Tactile Offset Shadow Disc */}
            <circle cx="64" cy="64" r="34" fill="#000000" opacity="0.55" />

            {/* Main Lunar Body */}
            <circle
              cx="60"
              cy="60"
              r="34"
              fill={theme.color.theme.color}
              stroke={theme.color.font.color}
              strokeWidth="2"
            />

            {/* Lunar Terminator Shading Arc */}
            <path
              d="M60,26 A34,34 0 0,1 60,94 A22,34 0 0,0 60,26 Z"
              fill={theme.color.background.accent}
            />

            {/* Technical Vector Lunar Craters (Matching Rocket Porthole Style) */}
            <circle
              cx="46"
              cy="46"
              r="7"
              fill={theme.color.background.accent}
              stroke={theme.color.font.color}
              strokeWidth="1.5"
            />
            <circle
              cx="70"
              cy="72"
              r="9"
              fill={theme.color.theme.color}
              stroke={theme.color.font.color}
              strokeWidth="1.5"
            />
            <circle
              cx="48"
              cy="74"
              r="4.5"
              fill={theme.color.background.accent}
              stroke={theme.color.font.color}
              strokeWidth="1.5"
            />

            {/* Equatorial Telemetry Grid Line */}
            <line
              x1="26"
              y1="60"
              x2="94"
              y2="60"
              stroke={theme.color.primary.color}
              strokeWidth="1.2"
              strokeDasharray="3 4"
              opacity="0.7"
            />
          </Box>
        </Box>
      </Box>

      {/* Meteor Shower (Strongest in Exosphere -> Mesosphere, thins out in Troposphere) */}
      <Box sx={{ opacity: Math.max(1 - scrollProgress * 1.15, 0.15) }}>
        {meteors.map((m) => (
          <Box
            key={m.id}
            sx={{
              position: "absolute",
              top: `${m.top}%`,
              left: `${m.left}%`,
              width: m.length,
              height: 1.5,
              background: theme.color.meteor.tail,
              borderRadius: "1px",
              animation: `wide-meteor ${m.duration}s infinite linear`,
              animationDelay: `${m.delay}s`,
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: -1,
                width: 3.5,
                height: 3.5,
                borderRadius: "50%",
                backgroundColor: theme.color.meteor.head,
                boxShadow: `0 0 6px 2px ${theme.color.meteor.glow}`,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
